import * as React from 'react';
import Box from '@mui/material/Box';
import {
  gridColumnVisibilityModelSelector,
  useGridApiContext,
  useGridRootProps,
  isLeaf,
  gridColumnLookupSelector,
  useGridSelector,
} from '@mui/x-data-grid';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';

const getColumnGroupLeaves = (group, callback) => {
  group.children.forEach((child) => {
    if (isLeaf(child)) {
      callback(child.field);
    } else {
      getColumnGroupLeaves(child, callback);
    }
  });
};

function ColumnGroup({ group, columnLookup, apiRef, columnVisibilityModel }) {
  const leaves = React.useMemo(() => {
    const fields = [];
    getColumnGroupLeaves(group, (field) => fields.push(field));
    return fields;
  }, [group]);

  const { isGroupChecked, isGroupIndeterminate } = React.useMemo(() => ({
    isGroupChecked: leaves.every((field) => columnVisibilityModel[field] !== false),
    isGroupIndeterminate:
      leaves.some((field) => columnVisibilityModel[field] === false) &&
      !leaves.every((field) => columnVisibilityModel[field] === false),
  }), [columnVisibilityModel, leaves]);

  const toggleColumnGroup = (checked) => {
    const newColumnVisibilityModel = { ...columnVisibilityModel };
    getColumnGroupLeaves(group, (field) => {
      newColumnVisibilityModel[field] = checked;
    });
    apiRef.current.setColumnVisibilityModel(newColumnVisibilityModel);
  };

  const toggleColumn = (field, checked) => {
    apiRef.current.setColumnVisibility(field, checked);
  };

  return (
    <div>
      <FormControlLabel
        control={
          <Checkbox
            checked={isGroupChecked}
            indeterminate={isGroupIndeterminate}
            size="small"
            sx={{ p: 1 }}
          />
        }
        label={group.headerName ?? group.groupId}
        onChange={(_, newValue) => toggleColumnGroup(newValue)}
      />
      <Box sx={{ pl: 3.5 }}>
        {group.children.map((child) => (
          isLeaf(child) ? (
            <Stack direction="row" key={child.field}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={columnVisibilityModel[child.field] !== false}
                    size="small"
                    sx={{ p: 1 }}
                  />
                }
                label={columnLookup[child.field]?.headerName ?? child.field}
                onChange={(_, newValue) => toggleColumn(child.field, newValue)}
              />
            </Stack>
          ) : (
            <ColumnGroup
              key={child.groupId}
              group={child}
              columnLookup={columnLookup}
              apiRef={apiRef}
              columnVisibilityModel={columnVisibilityModel}
            />
          )
        ))}
      </Box>
    </div>
  );
}

function ColumnsPanel(props) {
  const apiRef = useGridApiContext();
  const rootProps = useGridRootProps();
  const columnLookup = useGridSelector(apiRef, gridColumnLookupSelector);
  const columnVisibilityModel = useGridSelector(apiRef, gridColumnVisibilityModelSelector);
  const columnGroupingModel = rootProps.columnGroupingModel || [];

  // Get all columns used in the grid
  const allColumns = Object.keys(columnLookup);

  // Get columns that are part of a group
  const groupedColumns = new Set();
  columnGroupingModel.forEach((group) =>
    getColumnGroupLeaves(group, (field) => groupedColumns.add(field))
  );

  // Find columns that are NOT in any group
  const nonGroupedColumns = allColumns.filter((col) => !groupedColumns.has(col));

  // Show all columns
  const showAllColumns = () => {
    const newModel = {};
    allColumns.forEach((field) => {
      newModel[field] = true;
    });
    apiRef.current.setColumnVisibilityModel(newModel);
  };

  // Hide all columns
  const hideAllColumns = () => {
    const newModel = {};
    allColumns.forEach((field) => {
      newModel[field] = false;
    });
    apiRef.current.setColumnVisibilityModel(newModel);
  };

  // Reset to initial state (keeping non-preset columns untouched)
  const resetColumns = () => {
    const defaultModel = rootProps.initialState?.columns?.columnVisibilityModel || {};
    const newModel = { ...defaultModel };
    allColumns.forEach((field) => {
      if (!(field in newModel)) {
        newModel[field] = true; // Default behavior for non-preset columns
      }
    });
    apiRef.current.setColumnVisibilityModel(newModel);
  };

  return (
    <Box sx={{ px: 2, py: 0.5 }}>
      {/* Buttons for bulk actions */}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Button variant="text" size="small" onClick={showAllColumns}>
          Show All
        </Button>
        <Button variant="text" size="small" onClick={hideAllColumns}>
          Hide All
        </Button>
        <Button variant="text" size="small" onClick={resetColumns}>
          Reset
        </Button>
      </Stack>

      {/* Render grouped columns */}
      {columnGroupingModel.map((group) => (
        <ColumnGroup
          key={group.groupId}
          group={group}
          columnLookup={columnLookup}
          columnVisibilityModel={columnVisibilityModel}
          apiRef={apiRef}
        />
      ))}

      {/* Render non-grouped columns */}
      {nonGroupedColumns.length > 0 && (
        <div>
          <strong>Other Columns</strong>
          {nonGroupedColumns.map((field) => (
            <Stack direction="row" key={field}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={columnVisibilityModel[field] !== false}
                    size="small"
                    sx={{ p: 1 }}
                  />
                }
                label={columnLookup[field]?.headerName ?? field}
                onChange={(_, newValue) => apiRef.current.setColumnVisibility(field, newValue)}
              />
            </Stack>
          ))}
        </div>
      )}
    </Box>
  );
}

export default ColumnsPanel;
